import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  setDoc,
} from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import { PilgrimGuideState } from '../types';
import { COLLECTIONS } from '../constants';
import { checkGuideBan } from './banActions';
import { createDispute } from './disputeActions';
import { useBansStore } from '../../bansStore';
import { useSuspensionStore } from '../../suspensionStore';

export const linkPilgrimToGuideAction = async (
  pilgrimId: string,
  guideId: string,
  organizerId: string | null,
  set: (state: Partial<PilgrimGuideState>) => void,
  translations: any,
  isOwner: boolean = true
) => {
  set({ loading: true, error: null });
  try {
    // Check for service suspension first
    const isSuspended = await useSuspensionStore
      .getState()
      .checkServiceSuspension('linking');
    
    if (isSuspended) {
      throw new Error('خدمة الربط معلقة حالياً');
    }

    // Check for user ban
    const isUserBanned = await useBansStore
      .getState()
      .checkUserBans(guideId, 'linking');
    
    if (isUserBanned) {
      throw new Error('المرشد محظور من استخدام خدمة الربط');
    }

    // Get guide data
    const guideRef = doc(firestore, COLLECTIONS.GUIDES, guideId);
    const guideDoc = await getDoc(guideRef);
    
    if (!guideDoc.exists()) {
      throw new Error('Guide not found');
    }

    const guideData = guideDoc.data();
    const actualOrganizerId = organizerId || guideData.organizerId;

    // Check for existing links and disputes
    const linksRef = collection(firestore, COLLECTIONS.LINKS);
    const existingLinksQuery = query(linksRef, where('pilgrimId', '==', pilgrimId));
    const existingLinksSnapshot = await getDocs(existingLinksQuery);

    let existingLink;
    let disputeId: string | null = null;

    if (!existingLinksSnapshot.empty) {
      existingLink = existingLinksSnapshot.docs[0].data();

      if (existingLink.guideId === guideId) {
        throw new Error(
          translations?.linkPilgrimToGuideAction?.alreadyLinkedError ||
            'This pilgrim is already linked to you'
        );
      }

      const existingGuideRef = doc(firestore, COLLECTIONS.GUIDES, existingLink.guideId);
      const existingGuideDoc = await getDoc(existingGuideRef);

      if (existingGuideDoc.exists()) {
        const existingGuideData = existingGuideDoc.data();

        if (existingGuideData.organizerId === actualOrganizerId) {
          throw new Error(
            translations?.linkPilgrimToGuideAction?.sameOrganizerError ||
            'لا يمكن إنشاء نزاع - كلا المرشدين ينتميان لنفس المنظم'
          );
        }

        if (existingLink.disputeId) {
          const disputeRef = doc(firestore, COLLECTIONS.DISPUTES, existingLink.disputeId);
          const disputeDoc = await getDoc(disputeRef);

          if (disputeDoc.exists()) {
            const disputeData = disputeDoc.data();
            
            for (const disputingGuide of disputeData.disputingGuides) {
              const disputeGuideRef = doc(firestore, COLLECTIONS.GUIDES, disputingGuide.guideId);
              const disputeGuideDoc = await getDoc(disputeGuideRef);

              if (disputeGuideDoc.exists()) {
                const disputeGuideData = disputeGuideDoc.data();
                
                if (disputeGuideData.organizerId === actualOrganizerId) {
                  throw new Error(
                    translations?.linkPilgrimToGuideAction?.existingDisputeSameOrganizerError ||
                    'لا يمكن الانضمام للنزاع - يوجد مرشد من نفس المنظم في النزاع'
                  );
                }
              }
            }
          }
        }
      }
    }

    if (!actualOrganizerId) {
      throw new Error('No organizer ID found for guide');
    }

    // Check organizer ban
    const isOrganizerBanned = await useBansStore
      .getState()
      .checkUserBans(actualOrganizerId, 'linking');
    
    if (isOrganizerBanned) {
      throw new Error('المنظم محظور من استخدام خدمة الربط');
    }

    // Check for previous ban
    const isBanned = await checkGuideBan(guideId, pilgrimId);
    if (isBanned) {
      throw new Error(
        translations?.linkPilgrimToGuideAction?.banError ||
          'Cannot link this pilgrim due to a ban from a previous dispute'
      );
    }

    // Get pilgrim data
    const pilgrimRef = doc(firestore, COLLECTIONS.PILGRIMS, pilgrimId);
    const pilgrimDoc = await getDoc(pilgrimRef);
    
    if (!pilgrimDoc.exists()) {
      throw new Error('Pilgrim not found');
    }

    const pilgrimData = pilgrimDoc.data();

    // Get organizer data
    const organizerRef = doc(firestore, COLLECTIONS.ORGANIZERS, actualOrganizerId);
    const organizerDoc = await getDoc(organizerRef);
    
    if (!organizerDoc.exists()) {
      throw new Error('Organizer not found');
    }

    const organizerData = organizerDoc.data();

    const batch = writeBatch(firestore);

    if (existingLink) {
      disputeId = await createDispute(existingLink, guideId, pilgrimId);
      batch.update(doc(firestore, COLLECTIONS.LINKS, existingLinksSnapshot.docs[0].id), {
        status: 'disputed',
        disputeId,
        updatedAt: Date.now(),
      });
    }

    // ✅ SAFE: Sanitized link data
    const linkRef = doc(collection(firestore, COLLECTIONS.LINKS));
    const linkData = {
      id: linkRef.id,
      pilgrimId,
      pilgrimName: pilgrimData.Name ?? 'Unknown',
      passportNumber: pilgrimData.Passport_Number ?? 'Unknown',
      gender: (pilgrimData.Gender?.toLowerCase() ?? 'unknown') as 'male' | 'female' | 'unknown',
      residenceCountry: pilgrimData.Residence_Country ?? null,
      packageName: pilgrimData.package_nickName ?? null,
      pilgrimType: pilgrimData.Pilgrim_Type ?? null,
      nusukGroup: pilgrimData.nusuk_group ?? null,
      email: pilgrimData.Email ? String(pilgrimData.Email).trim() : null,
      mobileNumber: pilgrimData.Mobile_Number ? String(pilgrimData.Mobile_Number).trim() : null,
      guideId,
      guideName: guideData.name ?? 'Unknown Guide',
      organizerId: actualOrganizerId,
      organizerName: organizerData.name ?? 'Unknown Organizer',
      status: disputeId ? 'disputed' : 'approved',
      disputeId: disputeId || null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      isOwner: isOwner === false ? false : null
    };

    batch.set(linkRef, linkData);

    // ✅ FIX: PilgrimSelector update with safe fields
    const selectorRef = collection(firestore, 'PilgrimSelector');
    const selectorQuery = query(selectorRef, where('pilgrimId', '==', pilgrimId));
    const selectorSnapshot = await getDocs(selectorQuery);
    
    if (!selectorSnapshot.empty) {
      const selectorDoc = selectorSnapshot.docs[0];
      const selectorData = selectorDoc.data();
      
      if (linkData.status === 'approved' || selectorData.guideId === guideId) {
        batch.update(selectorDoc.ref, {
          guideId,
          guideName: guideData.name ?? 'Unknown Guide',
          organizerId: actualOrganizerId,
          organizerName: organizerData.name ?? 'Unknown Organizer',
          'guideData.name': guideData.name ?? 'Unknown Guide',
          'guideData.organizerId': actualOrganizerId,
          'organizerData.name': organizerData.name ?? 'Unknown Organizer',
          status: 'active',
          updatedAt: Date.now()
        });
      }
    }
    
    // ✅ FIX: operationlist - Safe fields with null fallbacks
    const operationListRef = collection(firestore, 'operationlist');
    const operationQuery = query(operationListRef, where('pilgrimId', '==', pilgrimId));
    const operationSnapshot = await getDocs(operationQuery);
    
    if (operationSnapshot.empty) {
      const operationRef = doc(collection(firestore, 'operationlist'));
      batch.set(operationRef, {
        id: operationRef.id,
        pilgrimId,
        pilgrimName: pilgrimData.Name ?? 'Unknown',
        passportNumber: pilgrimData.Passport_Number ?? 'Unknown',
        gender: (pilgrimData.Gender?.toLowerCase() ?? 'unknown') as 'male' | 'female' | 'unknown',
        residenceCountry: pilgrimData.Residence_Country ?? null,
        packageName: pilgrimData.package_nickName ?? null,
        guideId,
        guideName: guideData.name ?? 'Unknown Guide',
        organizerId: actualOrganizerId,
        organizerName: organizerData.name ?? 'Unknown Organizer',
        operatorId: null,
        operatorName: null,
        operatorOrganizerId: null,
        operatorOrganizerName: null,
        syncedAt: Date.now(),
        isOwner: isOwner === false ? false : null
      });
    } else {
      operationSnapshot.docs.forEach(doc => {
        const updateData = {
          pilgrimName: pilgrimData.Name ?? 'Unknown',
          passportNumber: pilgrimData.Passport_Number ?? 'Unknown',
          gender: (pilgrimData.Gender?.toLowerCase() ?? 'unknown') as 'male' | 'female' | 'unknown',
          residenceCountry: pilgrimData.Residence_Country ?? null,
          packageName: pilgrimData.package_nickName ?? null,
          guideId,
          guideName: guideData.name ?? 'Unknown Guide',
          organizerId: actualOrganizerId,
          organizerName: organizerData.name ?? 'Unknown Organizer',
          syncedAt: Date.now(),
          isOwner: isOwner === false ? false : null
        };
        batch.update(doc.ref, updateData);
      });
    }
    
    await batch.commit();

    set(state => ({
      links: {
        ...state.links,
        [linkData.id]: linkData
      },
      loading: false
    }));

  } catch (error: any) {
    console.error('Error linking pilgrim to guide:', error);
    set({
      error:
        error.message ||
        translations?.linkPilgrimToGuideAction?.linkError ||
        'Failed to link pilgrim to guide',
      loading: false,
    });
    throw error;
  }
};